/* assets/of-quick-order-core.js
 * Pure logic for the Quick Order Pad. NO DOM, NO network.
 * Dual export: browser sets window.OFQuickOrderCore; node:test uses module.exports.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.OFQuickOrderCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // Normalize typed part-number input: trim ends, collapse internal runs of
  // whitespace to a single space, uppercase (matching is case-insensitive).
  function normalizePartNumber(raw) {
    if (raw == null) return '';
    return String(raw).trim().replace(/\s+/g, ' ').toUpperCase();
  }

  // Strip hyphens/spaces for hyphenation-tolerant comparison.
  function canonical(s) {
    return normalizePartNumber(s).replace(/[\s-]/g, '');
  }

  // Candidate query forms to try against the Storefront API: the normalized
  // input, plus a hyphen/space-stripped form (tolerates "14-051" vs "14051").
  function partNumberVariants(raw) {
    var norm = normalizePartNumber(raw);
    var stripped = norm.replace(/[\s-]/g, '');
    var out = [];
    if (norm) out.push(norm);
    if (stripped && stripped !== norm) out.push(stripped);
    return Array.from(new Set(out));
  }

  // Rank resolved results so the best match surfaces first.
  // Score (higher wins): exact canonical SKU = 100; SKU prefix = 60;
  // SKU contains query = 40; name contains query = 20; else 0.
  // Empty/whitespace-only query: every result scores 0, original order kept.
  function rankResults(query, results) {
    var q = canonical(query);
    if (q === '') {
      return results.map(function (r) { return { r: r, score: 0 }; })
        .map(function (x) { return x.r; });
    }
    var qn = normalizePartNumber(query);
    function score(r) {
      var sku = canonical(r.sku);
      var title = (r.title || '').toUpperCase();
      if (sku && sku === q) return 100;
      if (sku && sku.indexOf(q) === 0) return 60;
      if (sku && sku.indexOf(q) !== -1) return 40;
      if (title.indexOf(qn) !== -1) return 20;
      return 0;
    }
    return results
      .map(function (r, i) { return { r: r, s: score(r), i: i }; })
      .sort(function (a, b) { return b.s - a.s || a.i - b.i; })
      .map(function (x) { return x.r; });
  }

  // Merge a line into the list: if its variantId already exists, increment qty;
  // otherwise append. Never mutates the input (returns a new array).
  function mergeLine(lines, line) {
    var copy = lines.map(function (l) { return Object.assign({}, l); });
    for (var i = 0; i < copy.length; i++) {
      if (copy[i].variantId === line.variantId) {
        copy[i].qty = copy[i].qty + line.qty;
        return copy;
      }
    }
    copy.push(Object.assign({}, line));
    return copy;
  }

  // Given a pooled qty and ascending tiers [{qty,pct}], return the active pct
  // (0 if below the first tier). Tiers need not be pre-sorted.
  function activeTierForQty(pooledQty, tiers) {
    var sorted = (tiers || []).slice().sort(function (a, b) { return a.qty - b.qty; });
    var pct = 0;
    for (var i = 0; i < sorted.length; i++) {
      if (pooledQty >= sorted[i].qty) pct = sorted[i].pct;
    }
    return pct;
  }

  // Per-line money. Production formula: discounted unit = round(price*(100-pct)/100),
  // line = unit * qty.
  function lineTotals(line, pct) {
    var qty = line.qty || 0;
    if (!(typeof pct === 'number' && isFinite(pct) && pct > 0)) {
      return { unitCents: line.priceCents, lineCents: line.priceCents * qty, discounted: false };
    }
    var unit = Math.round(line.priceCents * (100 - pct) / 100);
    return { unitCents: unit, lineCents: unit * qty, discounted: true };
  }

  // The bulk chip's words on a row: "10% bulk applied \u00b7 add 4 for 15%"
  // once a tier is reached, "add 2 more for 10% off" before it. One source
  // for the Quick Order pad's rows (assets/of-quick-order.js renderRowLabels)
  // and the list editor's rows (assets/of-lists-pad.js, 2026-10-26), so the
  // two read the same. pct: the tier reached (0 for none); pooled: the
  // rule's pooled quantity; next: of-nudges-core nextTier(...).next, or
  // null at the top tier. '' when there is nothing to say.
  function bulkLabel(pct, pooled, next) {
    var need = next ? next.qty - (pooled || 0) : 0;
    if (pct > 0) return pct + '% bulk applied' + (need > 0 ? ' \u00b7 add ' + need + ' for ' + next.pct + '%' : '');
    return need > 0 ? 'add ' + need + ' more for ' + next.pct + '% off' : '';
  }

  // Whole-order math. Pools qty per ruleSlug, resolves each pool's tier, then
  // applies that pct to every line in the pool. Returns subtotal, discount,
  // total, total qty, and a discounts list (one per rule group).
  // `rules` is a map slug -> { tiers:[{qty,pct}], ... }.
  function orderTotals(lines, rules, cartPool) {
    var poolQty = {};
    var i, l;
    for (i = 0; i < lines.length; i++) {
      l = lines[i];
      if (l.ruleSlug) poolQty[l.ruleSlug] = (poolQty[l.ruleSlug] || 0) + (l.qty || 0);
    }
    // Fold in cart quantities (per rule) so the active tier reflects cart + pad.
    // Amount stays computed on pad lines only (below); cart only lifts the pct.
    if (cartPool) {
      Object.keys(cartPool).forEach(function (slug) {
        poolQty[slug] = (poolQty[slug] || 0) + (cartPool[slug] || 0);
      });
    }
    var poolPct = {};
    Object.keys(poolQty).forEach(function (slug) {
      var rule = rules && rules[slug];
      poolPct[slug] = rule ? activeTierForQty(poolQty[slug], rule.tiers || []) : 0;
    });

    var subtotalCents = 0, totalQty = 0;
    var groupAmount = {}, groupLabel = {};
    for (i = 0; i < lines.length; i++) {
      l = lines[i];
      var pct = (l.ruleSlug && poolPct[l.ruleSlug]) || 0;
      var lt = lineTotals({ priceCents: l.priceCents, qty: l.qty }, pct);
      var base = l.priceCents * (l.qty || 0);
      subtotalCents += base;
      totalQty += (l.qty || 0);
      if (pct > 0) {
        groupAmount[l.ruleSlug] = (groupAmount[l.ruleSlug] || 0) + (base - lt.lineCents);
        groupLabel[l.ruleSlug] = l.ruleLabel || l.ruleSlug;
      }
    }
    var discounts = Object.keys(groupAmount).map(function (slug) {
      return { slug: slug, label: groupLabel[slug], pct: poolPct[slug], amountCents: groupAmount[slug], pooledQty: poolQty[slug] };
    });
    var discountCents = discounts.reduce(function (s, d) { return s + d.amountCents; }, 0);
    var totalCents = subtotalCents - discountCents;

    // Opportunities: groups present on the pad with tiers but still below their
    // first tier (pct 0). The footer surfaces these as an always-visible
    // "add N more to start saving" nudge.
    var padLabel = {};
    for (i = 0; i < lines.length; i++) {
      if (lines[i].ruleSlug) padLabel[lines[i].ruleSlug] = lines[i].ruleLabel || lines[i].ruleSlug;
    }
    var opportunities = Object.keys(padLabel).filter(function (slug) {
      var rule = rules && rules[slug];
      return rule && (rule.tiers || []).length && !poolPct[slug];
    }).map(function (slug) {
      return { slug: slug, label: padLabel[slug], pooledQty: poolQty[slug] || 0 };
    });

    return {
      totalQty: totalQty,
      subtotalCents: subtotalCents,
      discountCents: discountCents,
      totalCents: totalCents,
      discounts: discounts,
      opportunities: opportunities,
    };
  }

  // Mirror the PDP inventory rule for a client-resolved variant.
  // available = availableForSale (bool); qty = quantityAvailable (number|null);
  // lowThreshold = settings.low_stock_threshold. Returns { maxQty, label, level }.
  function inventoryState(available, qty, lowThreshold) {
    var t = (typeof lowThreshold === 'number' && lowThreshold >= 0) ? lowThreshold : 1;
    if (!available) return { maxQty: 0, label: 'Sold out', level: 'none' };
    if (typeof qty === 'number' && qty > 0) {
      if (qty <= t) return { maxQty: qty, label: 'Low stock - ' + qty + ' left', level: 'low' };
      return { maxQty: qty, label: qty + ' in stock', level: 'high' };
    }
    return { maxQty: 9999, label: 'In stock', level: 'continue' };
  }

  // Plan an "add all to cart" against the current cart.
  // cartItems: [{ id, key, quantity, title, image, properties, selling_plan_allocation }]
  //   (raw /cart.js items; id = variant id, key = the unique line item key)
  // padItems:  [{ variantId, qty, title, image, available, maxQty }]  (pad lines, qty>0)
  //
  // C1: each addable pad item is matched against the cart's "plain" line for
  // that variant (no custom properties/selling plan, the kind quick-order's
  // own /cart/add.js would create or increment), identified by KEY, never by
  // bare variant id. A variant that only appears in the cart via a
  // property-bearing/subscription line (a DIFFERENT kind of line entirely)
  // is treated as isNew, so it's never merged into or zeroed by mistake, a
  // variant id can be shared by multiple distinct cart lines; a key cannot.
  function buildAddAllPlan(cartItems, padItems) {
    cartItems = cartItems || [];
    padItems = padItems || [];
    var plainByVariant = {};
    cartItems.forEach(function (c) {
      var isPlain = !(c.properties && Object.keys(c.properties).length) && !c.selling_plan_allocation;
      if (isPlain && !plainByVariant[c.id]) plainByVariant[c.id] = c;
    });
    var addable = [];
    var skipped = [];
    padItems.forEach(function (p) {
      if (p.available === false) {
        skipped.push({ variantId: p.variantId, qty: p.qty, title: p.title });
        return;
      }
      var match = plainByVariant[p.variantId];
      var currentQty = match ? (match.quantity || 0) : 0;
      addable.push({
        variantId: p.variantId, qty: p.qty, title: p.title, image: p.image,
        currentQty: currentQty, newQty: currentQty + p.qty, isNew: !match,
        key: match ? match.key : null,
        maxQty: (typeof p.maxQty === 'number') ? p.maxQty : null,
      });
    });
    var padQtyByKey = {};
    addable.forEach(function (a) { if (a.key) padQtyByKey[a.key] = a.qty; });
    var cartLines = cartItems.map(function (c) {
      var padQty = padQtyByKey[c.key] || 0;
      return {
        id: c.id, key: c.key, quantity: c.quantity || 0, title: c.title, image: c.image,
        padQty: padQty, newQty: (c.quantity || 0) + padQty, affected: padQty > 0,
      };
    });
    var cartCount = cartItems.reduce(function (s, c) { return s + (c.quantity || 0); }, 0);
    return { addable: addable, skipped: skipped, cartLines: cartLines, cartCount: cartCount, cartEmpty: cartItems.length === 0 };
  }

  // C3: cap each addable item's ADD quantity against room remaining in the
  // cart for its specific matched line (maxQty - currentQty), for "Add to
  // my cart" (merge) specifically. Unlike Replace (which sets an ABSOLUTE
  // target and so only ever needs to respect maxQty on its own), merge
  // stacks the pad qty on top of whatever's already there, so the inventory
  // cap must account for it. Items with zero room move to `skipped`
  // (surfaced like an out-of-stock line); items that get shortened but not
  // zeroed stay addable with a `trimmed` flag. Pure; never mutates the input.
  function capForMerge(addable) {
    var kept = [];
    var trimmedOut = [];
    (addable || []).forEach(function (a) {
      var cap = (typeof a.maxQty === 'number') ? a.maxQty : null;
      var room = cap == null ? a.qty : Math.max(0, cap - (a.currentQty || 0));
      var addQty = Math.min(a.qty, room);
      if (addQty <= 0) {
        trimmedOut.push({ variantId: a.variantId, qty: a.qty, title: a.title });
        return;
      }
      kept.push(Object.assign({}, a, { qty: addQty, trimmed: addQty < a.qty }));
    });
    return { addable: kept, skipped: trimmedOut };
  }

  // C2: pure clamp used by setLineQty (of-quick-order.js), the ONE choke
  // point for every qty change (typed input, stepper, duplicate-resolve
  // merges). maxQty undefined/non-number defaults to 999 (inventory hasn't
  // resolved yet); never goes below 0.
  function clampLineQty(requestedQty, maxQty) {
    var cap = (typeof maxQty === 'number') ? maxQty : 999;
    return Math.max(0, Math.min(cap, requestedQty));
  }

  // R1: whether a resolveLoadedLines()-style response is still current, i.e.
  // no newer load has started since the request was fired. Trivial, but
  // named + exported so the generation-gate pattern is independently testable.
  function isCurrentGeneration(requestGen, currentGenNow) {
    return requestGen === currentGenNow;
  }

  // S3 (security fix round, 2026-07-26): the ONE audited escaper every
  // of_badge rule label (cart_label/label, a merchant metaobject field that a
  // nightly BOM/optimizer pipeline also writes to) must route through before
  // landing in an innerHTML template string in of-quick-order.js. Standard
  // 5-entity HTML escape; null/undefined coerce to ''.
  var HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return HTML_ESCAPE_MAP[c]; });
  }

  // S3: strict allowlist for a color value from an of_badge rule before it
  // reaches a style/custom-property value. Only well-formed 3/4/6/8-digit hex
  // colors pass; anything else (including empty/garbage) returns `fallback`.
  var HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
  function sanitizeColor(v, fallback) {
    if (typeof v === 'string' && HEX_COLOR_RE.test(v.trim())) return v.trim();
    return fallback;
  }

  // S7 (security fix round, 2026-07-26): of_quick_order_resolve_failed
  // (GA4) sends the shopper's raw typed search query. A customer can type
  // anything into that box, including their own email address (e.g.
  // "order confirmation was sent to jane@example.com"), which would
  // otherwise land unredacted in GA4/BigQuery. Redact BEFORE truncating
  // (not after), so a long email never survives partially, e.g. only the
  // local part, past a 40-char cut.
  // F1 (2026-09-06 review): /cart/update.js `note` is a whole-field REPLACE,
  // not an append, and the pad's own note box is server-rendered EMPTY. A
  // naive write therefore destroys whatever the shopper already typed on the
  // cart page or in the drawer (a PO number, "deliver after 3pm, dock B").
  // Merge instead: keep the cart's note, append the pad's note beneath it,
  // and never duplicate a line the cart's note already carries. Pure.
  function mergeCartNote(existingNote, padNote) {
    var existing = String(existingNote == null ? '' : existingNote).trim();
    var pad = String(padNote == null ? '' : padNote).trim();
    if (!pad) return existing;
    if (!existing) return pad;
    var already = existing.split('\n').some(function (l) { return l.trim() === pad; });
    if (already || existing === pad) return existing;
    return existing + '\n' + pad;
  }

  var EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
  function redactEmailLike(s) {
    return String(s == null ? '' : s).replace(EMAIL_RE, '[redacted-email]');
  }

  return {
    buildAddAllPlan: buildAddAllPlan,
    capForMerge: capForMerge,
    clampLineQty: clampLineQty,
    isCurrentGeneration: isCurrentGeneration,
    inventoryState: inventoryState,
    normalizePartNumber: normalizePartNumber,
    canonical: canonical,
    partNumberVariants: partNumberVariants,
    rankResults: rankResults,
    mergeLine: mergeLine,
    activeTierForQty: activeTierForQty,
    lineTotals: lineTotals,
    bulkLabel: bulkLabel,
    orderTotals: orderTotals,
    escapeHtml: escapeHtml,
    sanitizeColor: sanitizeColor,
    redactEmailLike: redactEmailLike,
    mergeCartNote: mergeCartNote,
  };
});
